import { Controller } from "@hotwired/stimulus"

const THEMES = ["light", "dark", "system"]

export default class extends Controller {
  static targets = [
    "trigger",
    "triggerLight",
    "triggerDark",
    "triggerSystem",
    "option",
    "check"
  ]

  static values = {
    current: { type: String, default: "system" }
  }

  connect() {
    this.currentValue = localStorage.getItem("theme") || "system"
    this.onSystemPreferenceChange = () => {
      if (this.currentValue === "system") this.apply()
    }
    this.systemPreferenceQuery = window.matchMedia("(prefers-color-scheme: dark)")
    this.systemPreferenceQuery.addEventListener("change", this.onSystemPreferenceChange)

    this.onTurboRender = () => this.updateUi()
    document.addEventListener("turbo:render", this.onTurboRender)
    document.addEventListener("turbo:load", this.onTurboRender)

    this.apply()
  }

  disconnect() {
    this.systemPreferenceQuery?.removeEventListener(
      "change",
      this.onSystemPreferenceChange
    )
    document.removeEventListener("turbo:render", this.onTurboRender)
    document.removeEventListener("turbo:load", this.onTurboRender)
  }

  currentValueChanged() {
    this.updateUi()
  }

  set(event) {
    const theme = event.currentTarget.dataset.themeMode
    if (!THEMES.includes(theme)) return

    this.currentValue = theme
    localStorage.setItem("theme", theme)
    this.apply()
  }

  apply() {
    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches
    const isDark =
      this.currentValue === "dark" ||
      (this.currentValue === "system" && prefersDark)

    const root = document.documentElement
    root.dataset.theme = isDark ? "standoutdark" : "standoutlight"
    root.classList.toggle("dark", isDark)
    this.updateUi()
    window.dispatchEvent(new CustomEvent("themechange", { detail: { isDark } }))
  }

  updateUi() {
    if (!this.hasTriggerTarget) return

    const mode = this.currentValue
    const label = this.optionTargets.find(
      (el) => el.dataset.themeMode === mode
    )?.dataset.themeLabel

    this.triggerTarget.setAttribute(
      "aria-label",
      label || this.triggerTarget.getAttribute("aria-label") || ""
    )
    this.triggerTarget.setAttribute("title", label || "")

    if (this.hasTriggerLightTarget) {
      this.triggerLightTarget.classList.toggle("hidden", mode !== "light")
    }
    if (this.hasTriggerDarkTarget) {
      this.triggerDarkTarget.classList.toggle("hidden", mode !== "dark")
    }
    if (this.hasTriggerSystemTarget) {
      this.triggerSystemTarget.classList.toggle("hidden", mode !== "system")
    }

    this.optionTargets.forEach((option, index) => {
      const selected = option.dataset.themeMode === mode
      option.setAttribute("aria-checked", selected ? "true" : "false")
      if (this.checkTargets[index]) {
        this.checkTargets[index].classList.toggle("hidden", !selected)
      }
    })
  }
}
